import { NavLink, Outlet } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Moon, RefreshCw, Settings, Sun, TrendingUp, Users } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useFounding, useGrowth, useHelpRequests, useOpsHealth, useReliability, useTickets } from '@/hooks/useConsoleData';
import { overBudgetBadge } from '@/lib/reliability-data';
import { actionItems } from '@/lib/today';
import { MemberSearch } from '@/components/console/MemberSearch';
import { APP_URL } from '@/constants/common';
import s from '@/pages/console/console.module.css';

/**
 * The eight pages, one bar. The Today badge is the count of open action
 * items, so the bar itself says whether there is anything to do.
 *
 * On a phone (under 640px) the bar becomes four tabs at the bottom, People,
 * Needs, Growth and Settings, and the header carries a Refresh button that
 * refetches whatever the open page is reading. The other pages are linked
 * from Settings. Nothing changes at 640px and above.
 */
const TABS = [
  { to: '/people', label: 'People', Icon: Users },
  { to: '/needs', label: 'Needs', Icon: MessageSquare },
  { to: '/growth', label: 'Growth', Icon: TrendingUp },
  { to: '/settings', label: 'Settings', Icon: Settings },
];

export function Shell() {
  const { admin, logout } = useAuthStore();
  const { theme, toggleTheme } = useUIStore();
  const qc = useQueryClient();
  const fetching = useIsFetching();
  const growth = useGrowth();
  const tickets = useTickets('new');
  const ops = useOpsHealth();
  // The Help badge is the count of help requests nobody has answered yet.
  const helpRequests = useHelpRequests('new');
  const helpBadge = helpRequests.data ? helpRequests.data.counts.new : null;
  const founding = useFounding();
  // The Founding badge is what Brian owes a founding member today or earlier (FOUNDING_MEMBERS_2026-10-17.md).
  const foundingOwed = founding.data ? founding.data.summary.owed : null;
  // The System badge is the count of red checks on the last watchtower run, or 1 when the watchtower itself is silent.
  const systemBadge = ops.data ? (ops.data.deadMan.silent ? 1 : (ops.data.latest?.checks.filter((c) => c.status === 'critical').length ?? 0)) : null;
  // The Reliability badge is the number of surfaces over their error budget (in freeze).
  const reliability = useReliability();
  const reliabilityBadge = overBudgetBadge(reliability.data);
  const open = growth.data
    ? actionItems({ people: growth.data.recentSignups, tickets: tickets.data?.tickets ?? [], doneKeys: new Set() }).filter((i) => !i.done).length
    : null;
  // Every query the open page has mounted, refetched now; the badges come along.
  const refresh = () => { void qc.refetchQueries({ type: 'active' }); };

  const link = (to: string, label: string, badge?: number | null) => (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => `${s.navLink} ${isActive ? s.navOn : ''}`}>
      {label}
      {typeof badge === 'number' && badge > 0 && <span className={s.navBadge}>{badge}</span>}
    </NavLink>
  );

  return (
    <div className={s.shell}>
      <header className={s.topbar}>
        <div className={s.brand}>
          <span className={s.brandEyebrow}>Admin</span>
          <span className={s.brandName}>PostureVision®</span>
        </div>
        <nav className={s.nav} aria-label="Console">
          {link('/', 'Today', open)}
          {link('/business', 'Business')}
          {link('/people', 'People')}
          {link('/needs', 'Needs')}
          {link('/help', 'Help', helpBadge)}
          {link('/founding', 'Founding', foundingOwed)}
          {link('/messages', 'Messages')}
          {link('/product', 'Product')}
          {link('/growth', 'Growth')}
          {link('/system', 'System', systemBadge)}
          {link('/reliability', 'Reliability', reliabilityBadge)}
          {link('/monitoring', 'Monitoring')}
          {link('/settings', 'Settings')}
        </nav>
        <MemberSearch />
        <div className={s.topRight}>
          <span className={s.who}>{admin?.email ?? ''}</span>
          <button type="button" className={`${s.phoneOnly} ${s.refreshBtn}`} onClick={refresh} disabled={fetching > 0} aria-busy={fetching > 0} aria-label="Refresh this page" title="Refresh this page">
            <RefreshCw size={14} className={fetching > 0 ? s.spin : undefined} />
            <span>{fetching > 0 ? 'Refreshing' : 'Refresh'}</span>
          </button>
          <button type="button" onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle theme">
            {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <a className={s.desktopOnly} href={APP_URL} target="_blank" rel="noreferrer">Open the app</a>
          <button type="button" className={s.desktopOnly} onClick={logout}>Sign out</button>
        </div>
      </header>
      <main className={s.main}>
        <Outlet />
        <footer className={s.foot}>Powered by <strong>PostureVision®</strong> · admin · live data · nothing here is estimated</footer>
      </main>
      <nav className={s.tabBar} aria-label="Pages">
        {TABS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `${s.tab} ${isActive ? s.tabOn : ''}`}>
            <Icon size={22} aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
